import React, { useState, useEffect, useRef } from 'react';
import { FileText } from 'lucide-react';
import { appendTokenToUrl } from '../../api/courseService';
import { updateLessonWatchTime } from '../../api/packageService';
// Import react-pdf components
import { Document, Page, pdfjs } from 'react-pdf';

// Worker loading configuration with multiple fallbacks
const setupPDFWorker = () => {
  try {
    // Use local worker first if available
    const localWorker = new Worker(
      new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url),
      { type: 'module' }
    );
    pdfjs.GlobalWorkerOptions.workerPort = localWorker;
    console.log('Using local PDF worker');
  } catch (error) {
    console.warn('Local PDF worker not available, trying CDN...');
    
    // Try different CDN sources
    const cdnSources = [
      `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`,
      `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`,
      `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`
    ];
    
    // Set the worker source
    pdfjs.GlobalWorkerOptions.workerSrc = cdnSources[0];
    
    // Pre-load worker with error handling
    const originalWorkerSrc = pdfjs.GlobalWorkerOptions.workerSrc;
    const workerScript = document.createElement('script');
    workerScript.src = originalWorkerSrc;
    workerScript.onerror = () => {
      console.warn(`Failed to load worker from ${originalWorkerSrc}, trying fallback...`);
      
      // Try next CDN source
      for (let i = 1; i < cdnSources.length; i++) {
        try {
          pdfjs.GlobalWorkerOptions.workerSrc = cdnSources[i];
          console.log(`Using fallback worker source: ${cdnSources[i]}`);
          break;
        } catch (fallbackError) {
          console.warn(`Fallback ${i} also failed:`, fallbackError);
        }
      }
    };
    
    // Add script tag to trigger loading
    document.head.appendChild(workerScript);
    setTimeout(() => document.head.removeChild(workerScript), 1000);
  }
};

// Initialize PDF worker on component mount
let workerInitialized = false;

interface DocumentViewerProps {
  url: string;
  title: string;
  description?: string;
  // Add props for tracking lesson view time
  userId?: string;
  courseId?: string;
  lessonId?: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ url, title, description, userId, courseId, lessonId }) => {
  const [processedUrl, setProcessedUrl] = React.useState<string>('');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Track view time
  const viewTimeRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(Date.now());
  // State for fallback rendering
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const [pdfWorkerReady, setPdfWorkerReady] = useState(false);
  
  // Initialize PDF worker once
  useEffect(() => {
    if (!workerInitialized) {
      try {
        setupPDFWorker();
        workerInitialized = true;
        setPdfWorkerReady(true);
      } catch (error) {
        console.error('Failed to initialize PDF worker:', error);
        setPdfWorkerReady(false);
      }
    }
  }, []);
  
  React.useEffect(() => {
    // Process the URL to add authentication token
    if (url) {
      const tokenizedUrl = appendTokenToUrl(url);
      setProcessedUrl(tokenizedUrl || url);
    }
  }, [url]);
  
  // Track document view time
  useEffect(() => {
    if (!userId || !courseId || !lessonId) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = (now - lastUpdateTimeRef.current) / 1000; // Convert to seconds
      viewTimeRef.current += elapsed;
      lastUpdateTimeRef.current = now;
      
      // Update every 10 seconds
      if (viewTimeRef.current >= 10) {
        sendViewTimeUpdate();
      }
    }, 1000);
    
    return () => {
      clearInterval(interval);
      // Send final update when component unmounts
      if (viewTimeRef.current > 0) {
        sendViewTimeUpdate();
      }
    };
  }, [userId, courseId, lessonId]);
  
  // Send view time update to backend
  const sendViewTimeUpdate = async () => {
    if (!userId || !courseId || !lessonId || viewTimeRef.current <= 0) return;
    
    try {
      const response = await updateLessonWatchTime(
        userId, 
        courseId, 
        lessonId, 
        Math.floor(viewTimeRef.current)
      );
      
      if (response.status === 200) {
        console.log('Document view time updated successfully');
        viewTimeRef.current = 0; // Reset after successful update
      } else {
        console.error('Failed to update document view time:', response.message);
      }
    } catch (error) {
      console.error('Error sending view time update:', error);
    }
  };

  // Determine if the document can be viewed in browser
  const canPreviewInBrowser = (url: string): boolean => {
    if (!url) return false;
    
    // Common document extensions that can be previewed
    const previewableExtensions = [
      '.pdf', '.txt', '.html', '.htm', '.xml', 
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp',
      '.csv', '.md'
    ];
    
    // Check if URL ends with a previewable extension
    const hasExtension = previewableExtensions.some(ext => 
      url.toLowerCase().endsWith(ext) || 
      url.toLowerCase().includes(`.${ext}?`) ||
      url.toLowerCase().includes(`.${ext}&`)
    );
    
    // If no extension, check if it might be a PDF (common case)
    if (!hasExtension) {
      // Check if URL contains PDF-related patterns
      return url.toLowerCase().includes('pdf') || 
             url.toLowerCase().includes('/document/') ||
             url.toLowerCase().includes('/file/');
    }
    
    return hasExtension;
  };

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
  }

  function onDocumentLoadError(error: Error): void {
    console.error('Error loading PDF:', error);
    setError('Failed to load document. Please try again later.');
    setLoading(false);
    
    // Try fallback options
    setUseIframeFallback(true);
  }

  // Get appropriate preview element based on file type
  const renderPreview = () => {
    if (!processedUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-64 bg-black rounded-b-lg">
          <div className="bg-white p-3 rounded-full shadow mb-3">
            <FileText className="text-gray-400" size={24} />
          </div>
          <p className="text-gray-300 text-sm">No document available</p>
        </div>
      );
    }

    // Check if it's an image
    const isImage = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(processedUrl) || 
                   processedUrl.includes('image') ||
                   processedUrl.includes('photo');
    
    if (isImage) {
      return (
        <div className="flex justify-center items-center h-full bg-black p-4">
          <img 
            src={processedUrl} 
            alt={title} 
            className="max-w-full max-h-full object-contain rounded-lg"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/placeholder.svg";
            }}
          />
        </div>
      );
    }

    // For PDF documents
    const isPdf = processedUrl.toLowerCase().includes('.pdf') || 
                  processedUrl.toLowerCase().includes('pdf') ||
                  processedUrl.toLowerCase().includes('/document/');
    
    if ((canPreviewInBrowser(processedUrl) || isPdf) && isPdf) {
      // Use iframe fallback if specified or if PDF worker isn't ready
      if (useIframeFallback || error || !pdfWorkerReady) {
        return (
          <div className="relative w-full h-full rounded-b-lg overflow-hidden bg-black">
            <div className="w-full h-full flex flex-col">
              <div className="flex-1 overflow-auto">
                <iframe 
                  src={`${processedUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  title={title}
                  className="w-full h-full border-0"
                  onError={(e) => {
                    console.error('Iframe loading error:', e);
                    setError('Unable to display document in browser. Please download the file.');
                  }}
                />
              </div>
              <div className="bg-gray-800 p-2 text-center text-white text-sm flex justify-center space-x-4">
                <button 
                  onClick={() => window.open(processedUrl, '_blank')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Open PDF in new tab
                </button>
                <button 
                  onClick={() => {
                    // Try to download the file directly
                    const link = document.createElement('a');
                    link.href = processedUrl;
                    link.download = title || 'document.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="text-green-400 hover:text-green-300 underline"
                >
                  Download File
                </button>
                {pdfWorkerReady && (
                  <button 
                    onClick={() => setUseIframeFallback(false)}
                    className="text-yellow-400 hover:text-yellow-300 underline"
                  >
                    Try PDF Viewer
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      }
      
      // Use react-pdf viewer
      return (
        <div className="relative w-full h-full rounded-b-lg overflow-hidden bg-black">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
              <div className="text-white">Loading document...</div>
            </div>
          )}
          
          {error ? (
            <div className="flex flex-col items-center justify-center h-full bg-black p-6 text-center rounded-b-lg">
              <div className="bg-white p-3 rounded-full shadow mb-3">
                <FileText className="text-red-600" size={24} />
              </div>
              <h3 className="text-base font-bold text-white mb-1">Error Loading Document</h3>
              <p className="text-gray-300 text-sm max-w-xs">{error}</p>
              <div className="flex space-x-2 mt-4">
                <button 
                  onClick={() => window.open(processedUrl, '_blank')}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Open in New Tab
                </button>
                <button 
                  onClick={() => setUseIframeFallback(true)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                >
                  Try Browser Viewer
                </button>
              </div>
            </div>
          ) : (
            <div className="w-full h-full flex flex-col">
              <div className="flex-1 overflow-auto">
                <Document
                  file={processedUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={<div className="flex items-center justify-center h-full text-white">Loading PDF...</div>}
                  error={<div className="flex flex-col items-center justify-center h-full text-white">
                    <p>Failed to load PDF viewer</p>
                    <p className="text-sm mt-2">Don't worry, you can still access the document</p>
                    <div className="flex space-x-2 mt-3">
                      <button 
                        onClick={() => window.open(processedUrl, '_blank')}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Open in New Tab
                      </button>
                      <button 
                        onClick={() => {
                          // Try to download the file directly
                          const link = document.createElement('a');
                          link.href = processedUrl;
                          link.download = title || 'document.pdf';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
                      >
                        Download File
                      </button>
                      <button 
                        onClick={() => setUseIframeFallback(true)}
                        className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700 transition-colors"
                      >
                        Try Browser Viewer
                      </button>
                    </div>
                  </div>}
                  noData={<div className="flex items-center justify-center h-full text-white">No PDF file specified</div>}
                >
                  <Page 
                    pageNumber={pageNumber} 
                    width={window.innerWidth > 768 ? 800 : window.innerWidth - 40}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                </Document>
              </div>
              
              {numPages && numPages > 1 && (
                <div className="bg-gray-800 p-2 flex justify-center items-center">
                  <button 
                    onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                    disabled={pageNumber <= 1}
                    className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="mx-4 text-white">
                    Page {pageNumber} of {numPages}
                  </span>
                  <button 
                    onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages!))}
                    disabled={pageNumber >= numPages!}
                    className="px-3 py-1 bg-gray-700 text-white rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
              
              <div className="bg-gray-800 p-2 text-center text-white text-sm flex justify-center space-x-4">
                <button 
                  onClick={() => window.open(processedUrl, '_blank')}
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Open PDF in new tab
                </button>
                <button 
                  onClick={() => setUseIframeFallback(true)}
                  className="text-yellow-400 hover:text-yellow-300 underline"
                >
                  Try Browser Viewer
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    // For documents that can't be previewed, show message with option to open in new tab
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-black p-6 text-center rounded-b-lg">
        <div className="bg-white p-3 rounded-full shadow mb-3">
          <FileText className="text-blue-600" size={24} />
        </div>
        <h3 className="text-base font-bold text-white mb-1">Document Preview Unavailable</h3>
        <p className="text-gray-300 text-sm max-w-xs mb-4">
          This document format cannot be previewed in the browser.
        </p>
        <button 
          onClick={() => window.open(processedUrl, '_blank')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Open in New Tab
        </button>
      </div>
    );
  };

  return (
    <div className="w-full bg-black rounded-lg overflow-hidden">
      <div className="flex items-center px-4 py-3 border-b border-gray-700 bg-gray-900">
        <div className="bg-blue-900 p-2 rounded-lg mr-3">
          <FileText className="text-blue-300" size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-white truncate">{title}</h2>
          {description && (
            <p className="text-sm text-gray-300 truncate mt-1">{description}</p>
          )}
        </div>
      </div>
      
      <div className="h-[500px] w-full">
        {renderPreview()}
      </div>
    </div>
  );
};

export default DocumentViewer;